import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Search, X, Check } from 'lucide-react';
import { useClient } from '../../context/ClientContext';
import { useTheme } from '../../context/ThemeContext';
import ClientAvatar from '../admin/ClientAvatar';
import { TOPBAR_CLIENT_PICKED_EVENT } from '../tour/tourElementUtils';

export default function ClientSwitcherModal({ isOpen, onClose }) {
  const { clients, selectedClient, setSelectedClient } = useClient();
  const { isDarkMode } = useTheme();
  const [query, setQuery] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const [language, setLanguage] = useState(() => localStorage.getItem('language') || 'english');
  const direction = language === 'hebrew' ? 'rtl' : 'ltr';

  useEffect(() => {
    const handleStorageChange = () => {
      setLanguage(localStorage.getItem('language') || 'english');
    };
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('languageChange', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('languageChange', handleStorageChange);
    };
  }, []);

  const t = (en, he) => (language === 'hebrew' ? he : en);

  const pulseRank = (p) => ({ healthy: 0, new: 1, '24h': 2, '72h': 3, never: 4, onboarding: 5 }[p] ?? 6);

  const filteredClients = useMemo(() => {
    let list = Array.isArray(clients) ? clients : [];
    if (query.trim()) {
      const q = query.toLowerCase().trim();
      list = list.filter((c) => {
        const name = (c.full_name || c.name || '').toLowerCase();
        const email = (c.email || '').toLowerCase();
        const code = (c.user_code || '').toLowerCase();
        const phone = (c.phone_number || '').toLowerCase();
        return name.includes(q) || email.includes(q) || code.includes(q) || phone.includes(q);
      });
    }
    return [...list].sort((a, b) => pulseRank(a.engagementPulse) - pulseRank(b.engagementPulse));
  }, [clients, query]);

  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setHighlightedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  const handleSelect = (client) => {
    if (!client) return;
    setSelectedClient(client);
    window.dispatchEvent(new CustomEvent(TOPBAR_CLIENT_PICKED_EVENT, { detail: client }));
    onClose();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1 < filteredClients.length ? prev + 1 : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev - 1 >= 0 ? prev - 1 : filteredClients.length - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filteredClients[highlightedIndex]) {
        handleSelect(filteredClients[highlightedIndex]);
      }
    }
  };

  useEffect(() => {
    if (listRef.current && filteredClients.length > 0) {
      const activeEl = listRef.current.children[highlightedIndex];
      if (activeEl?.scrollIntoView) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, filteredClients.length]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('Switch Client', 'החלפת לקוח')}
      className="fixed inset-0 z-[120] flex items-start justify-center pt-16 sm:pt-24 px-4 bg-black/60  animate-in fade-in duration-150"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      dir={direction}
    >
      <div
        className={`w-full max-w-xl rounded-lg border  overflow-hidden transition-all duration-150 ${
          isDarkMode ? 'bg-surface-dark border-line-dark text-white' : 'bg-surface border-line text-ink'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Input Bar */}
        <div className="relative flex items-center px-4 py-3.5 border-b border-rule">
          <Search size={18} className="text-ink-muted dark:text-ink-muted-dark shrink-0" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('Search client by name, email, code or phone...', 'חיפוש לקוח לפי שם, אימייל, קוד או טלפון...')}
            className="w-full bg-transparent px-3 text-sm sm:text-base outline-none placeholder:text-ink-muted dark:placeholder:text-ink-muted-dark"
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              className="p-1 rounded-md text-ink-muted hover:text-ink dark:hover:text-white"
              aria-label={t('Clear search', 'נקה חיפוש')}
            >
              <X size={16} />
            </button>
          )}
          <kbd className="hidden sm:inline-flex items-center gap-0.5 px-2 py-0.5 text-[11px] font-mono font-medium rounded border border-rule text-ink-muted dark:text-ink-muted-dark bg-canvas dark:bg-canvas-dark shrink-0 ms-2">
            ESC
          </kbd>
        </div>

        {/* Results List */}
        <div
          ref={listRef}
          role="listbox"
          className="max-h-[min(60vh,380px)] overflow-y-auto p-2 overscroll-contain space-y-1"
        >
          {filteredClients.length > 0 ? (
            filteredClients.map((client, idx) => {
              const isSelected = selectedClient?.id === client.id;
              const isHighlighted = highlightedIndex === idx;
              const name = client.full_name || client.name || client.user_code || t('Unknown Client', 'לקוח');

              return (
                <button
                  key={client.id || client.user_code || idx}
                  role="option"
                  aria-selected={isSelected}
                  type="button"
                  onClick={() => handleSelect(client)}
                  onMouseEnter={() => setHighlightedIndex(idx)}
                  className={`w-full text-start px-3 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-3 cursor-pointer ${
                    isHighlighted
                      ? isDarkMode
                        ? 'bg-forest/20 text-white'
                        : 'bg-forest-faint text-ink'
                      : isDarkMode
                      ? 'text-ink-muted-dark hover:bg-surface-dark'
                      : 'text-ink hover:bg-canvas'
                  }`}
                >
                  <ClientAvatar client={client} size="sm" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-bold truncate text-sm">{name}</span>
                      {client.engagementPulse === 'healthy' && (
                        <span className="shrink-0 text-[10px] font-semibold px-2 py-0.5 rounded-full bg-forest-faint text-forest dark:bg-forest/20 dark:text-forest-ondark">
                          {t('Active <24h', 'פעיל ב-24 שעות')}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-ink-muted dark:text-ink-muted-dark truncate flex items-center gap-2 mt-0.5">
                      {client.phone_number && <span>{client.phone_number}</span>}
                      {client.email && (
                        <>
                          <span>·</span>
                          <span className="truncate">{client.email}</span>
                        </>
                      )}
                      {client.user_code && (
                        <>
                          <span>·</span>
                          <span className="font-mono">{client.user_code}</span>
                        </>
                      )}
                    </div>
                  </div>
                  {isSelected && (
                    <Check size={18} className="text-forest dark:text-forest-ondark shrink-0" />
                  )}
                </button>
              );
            })
          ) : (
            <div className="py-10 text-center text-sm text-ink-muted dark:text-ink-muted-dark">
              {t('No clients match your search.', 'לא נמצאו לקוחות מתאימים לחיפוש.')}
            </div>
          )}
        </div>

        {/* Footer Hint */}
        <div className="px-4 py-2.5 border-t border-rule bg-canvas/40 dark:bg-canvas-dark/40 flex items-center justify-between text-xs text-ink-muted dark:text-ink-muted-dark">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded border border-rule text-[10px] font-mono bg-canvas dark:bg-canvas-dark">↑↓</kbd>
              {t('Navigate', 'ניווט')}
            </span>
            <span className="inline-flex items-center gap-1">
              <kbd className="px-1.5 py-0.5 rounded border border-rule text-[10px] font-mono bg-canvas dark:bg-canvas-dark">↵</kbd>
              {t('Select', 'בחירה')}
            </span>
          </div>
          <span>{filteredClients.length} {t('clients', 'לקוחות')}</span>
        </div>
      </div>
    </div>
  );
}
